<template>
    <div class="fill-height of-y">
        <v-scroll-x-transition hide-on-leave>
            <app-centre-loader v-if="loading" label="Fetching Data"/>
        </v-scroll-x-transition>

        <v-scroll-x-transition hide-on-leave>
            <div v-if="!loading" class="fill-height of-y">
                <div class="fill-height d-flex align-center justify-center overline amber--text" v-if="!stats.length">
                    No Data For Given Range
                </div>
                <div v-else class="fill-height of-y">
                    <v-data-table
                            ref="table"
                            :headers="viewColumns"
                            :items="stats"
                            :search="$store.state.ref.dashboard.filterStr"
                            :items-per-page="stats.length"
                            class="fill-height of-y full-table"
                            hide-default-footer
                            fixed-header
                            @click:row="gotoAppStats($event.appName)"
                            :sortBy.sync="$store.state.sortPreference.dashboard.apps.by"
                            :sort-desc.sync="$store.state.sortPreference.dashboard.apps.order"
                            item-key="_id"
                    >

                        <template v-slot:item.appName="{ item }">
                            <div class="d-flex align-center overline" style="white-space: nowrap">
                                <app-icon :key="item._id + item.appName" :match="item.appName" left small/>
                                {{ item.appName }}
                            </div>
                        </template>

<!--label-->
                        <template v-slot:item.label="{ item }">
                            <div class="d-flex align-center overline" :class="`${labelsRef[item.label].color}--text`">
                                <v-icon small :color="labelsRef[item.label].color" left>{{labelsRef[item.label].icon}}</v-icon>
                                {{labelsRef[item.label].text}}
                            </div>
                        </template>

                        <template v-slot:item.tt="{ item }">{{ hoursDisplay(item.tt, true) }}</template>
                        <template v-slot:item.ttn="{ item }">{{ hoursDisplay(item.ttn, true) }}</template>
                        <template v-slot:item.tto="{ item }">{{ hoursDisplay(item.tto, true) }}</template>

<!--count-->


                        <template v-slot:item.actions="{ item }">
                            <v-btn
                                    color="primary"
                                    class="mx-2"
                                    small
                                    icon
                                    :loading="loadingClassifications.includes(item.appName)"
                                    @click.stop="classificationMenuClicked($event, item)"
                            >
                                <v-icon>mdi-cog</v-icon>
                            </v-btn>
                        </template>

                    </v-data-table>
                </div>
            </div>
        </v-scroll-x-transition>

        <app-dlg
                v-if="$store.state.ref.dashboard.showColumnSelection"
                :show-dlg.sync="$store.state.ref.dashboard.showColumnSelection"
                title="Select Columns"
                :default-padding="false"
        >

            <app-list
                    v-model="$store.state.ref.dashboard.appsSelectedColumns"
                    :items="headers"
                    :multiple="true"
                    headline-key="text"
                    item-key="value"
                    sub-line-key="description"
                    :show-check-boxes="true"
                    :return-object="false"
                    class="fill-height of-y"
                    :height="400"
                    @input="updateSelections"
            />

            <template v-slot:actions>
                <v-btn
                        @click="$store.state.ref.dashboard.appsSelectedColumns = [...$store.state.defaultSelections.appsSelectedColumns]; updateSelections()"
                        small
                        class="ml-2"
                        color="blue"
                        outlined
                        size="small"
                >
                    Reset Selections
                </v-btn>
                <v-btn
                        @click="$store.state.ref.dashboard.showColumnSelection = false"
                        small
                        class="ml-2"
                        color="secondary"
                        variant="contained"
                        size="small"
                >
                    Done
                </v-btn>
            </template>
        </app-dlg>

        <v-menu
                v-if="showClassificationMenu"
                v-model="showClassificationMenu"
                :position-x="classificationX"
                :position-y="classificationY"
                absolute
                offset-y
                :max-width="200"
                :min-width="200"
        >
            <v-card>
                <v-list dense>
                    <v-list-item-group
                            :value="selectedClassification.label"
                            color="primary"
                    >
                        <v-list-item
                                v-for="label in labels"
                                :value="label.id"
                                :key="label.id"
                                @click="changeLabel(label.id)"
                                active-color="primary"
                        >
                            <v-list-item-content>
                                <v-list-item-title v-text="label.text"></v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list-item-group>
                    <v-list-item
                            @click="gotoApp(selectedClassification.appName)"
                            active-color="primary"
                    >
                        <v-list-item-content>
                            <v-list-item-title>More Options</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
            </v-card>
        </v-menu>

        <app-classification-dlg
                ref="classificationDlg"
                @future="saveClassificationLabelChangesConfirm()"
                @history="saveClassificationLabelChangesConfirm(true)"
        />

    </div>
</template>

<script>

import AppCentreLoader from "@/components/Shared/AppCentreLoader";
import {
    percentDisplay,
    hourDisplay,
    hourDisplayWithoutUnit,
    isValidUrl,
    exportStats,
    arrSplice
} from "@/core/codeFunctions";
import AppDlg from "@/components/Shared/AppDlg";
import AppList from "@/components/Shared/AppList/AppList";
import {mapActions, mapGetters} from "vuex";
import AppIcon from "@/components/Shared/AppIcon";
import {format, isSameDay, isSameMonth, isSameYear} from "date-fns";
import AppClassificationDlg from "@/components/Shared/AppClassificationDlg";
import {StatusMap} from "@/models/statusMap";

export default {
    components: {AppClassificationDlg, AppIcon, AppList, AppDlg, AppCentreLoader},
    props: {
        vm: Object
    },
    data() {
        return {
            hoursDisplay: hourDisplay,
            percentDisplay: percentDisplay,
            loading: false,
            stats: [],
            headers: [
                {text: 'App', value: 'appName', description: 'App Or Website'},
                {text: 'Label', value: 'label', description: 'Productivity Label'},
                {text: 'Active Time', value: 'tt', description: 'Total time active'},
                {text: 'Active Normal Time', value: 'ttn', description: 'Total time active in normal hours'},
                {text: 'Active Overtime', value: 'tto', description: 'Total time active in overtime hours'},
                {text: 'Usage Count', value: 'count', description: 'Total times app was used'},
            ],
            isValidUrl: isValidUrl,

            loadingClassifications: [],
            showClassificationMenu: false,
            selectedClassification: null,
            selectedClassificationNewLabel: null,
            classificationX: 0,
            classificationY: 0,
        }
    },
    watch: {
        "$store.state.ref.dashboard.selectedRange.start": {
            immediate: true,
            handler(val) {
                this.refreshData()
            }
        },
        devicesWithAccess(val) {
            this.refreshData()
        }
    },
    mounted() {
        // this.refreshData();

    },
    methods: {
        async exportDataDetail() {
            try {
                this.vm.$refs.navBarBody.$refs.dashboardToolbar.showLoadingExport = true;
                const stats = await this.$newReq('POST', 'dashboard/getAppsStatsDetail', {
                    start: this.start,
                    end: this.end,
                })

                const dateTitle = (() => {
                    if (!isSameYear(this.$store.state.ref.dashboard.selectedRange.start, this.$store.state.ref.dashboard.selectedRange.end))
                        return `${format(this.$store.state.ref.dashboard.selectedRange.start,'dd MMM yyyy')} - ${format(this.$store.state.ref.dashboard.selectedRange.end,'dd MMM yyyy')}`
                    if (!isSameMonth(this.$store.state.ref.dashboard.selectedRange.start, this.$store.state.ref.dashboard.selectedRange.end))
                        return `${format(this.$store.state.ref.dashboard.selectedRange.start,'dd MMM')} - ${format(this.$store.state.ref.dashboard.selectedRange.end,'dd MMM yyyy')}`
                    if (!isSameDay(this.$store.state.ref.dashboard.selectedRange.start, this.$store.state.ref.dashboard.selectedRange.end))
                        return `${format(this.$store.state.ref.dashboard.selectedRange.start,'dd')} - ${format(this.$store.state.ref.dashboard.selectedRange.end,'dd MMM yyyy')}`
                    return format(this.$store.state.ref.dashboard.selectedRange.end,'dd MMM yyyy')
                })()

                exportStats(
                        `Tracklogix Apps ${dateTitle} Detail`,
                        'Apps',
                        [{text: "Date", value: "date"}, ...this.$refs.table.headers],
                        stats,
                        this.$store.getters.labelsRef
                )
            } catch (e) {
                this.$snack.netWorkError();
            } finally {
                this.vm.$refs.navBarBody.$refs.dashboardToolbar.showLoadingExport = false;
            }
        },

        ...mapActions(['gotoAppStats', 'gotoApp', 'updateSelections']),

        async changeLabel(labelId) {
            this.selectedClassificationNewLabel = labelId

            if (this.$store.state.historyPreference)
                this.saveClassificationLabelChangesConfirm(this.$store.state.historyPreference === 'history').catch(console.log)
            else {
                this.$refs.classificationDlg.showDlg()
            }
        },

        async saveClassificationLabelChangesConfirm(updateHistorical = false) {
            const selectedClassification = this.selectedClassification
            const loadingMatch = selectedClassification.appName
            const defaultLabel = this.selectedClassificationNewLabel
            try {
                this.loadingClassifications.push(selectedClassification.appName)
                const editMap = new StatusMap({match: selectedClassification.appName, defaultLabel: defaultLabel})
                const {_id} = await editMap.updateDefault(updateHistorical);
                selectedClassification.label = defaultLabel
                arrSplice(this.loadingClassifications, (match) => match === loadingMatch)
                // if (updateHistorical)
                //     this.refreshData(false)
            } catch (e) {
                this.$snack.netWorkError()
                console.log(e)
                arrSplice(this.loadingClassifications, (match) => match === loadingMatch)
            }
        },

        classificationMenuClicked(e, classification) {
            this.selectedClassification = classification;
            const params = e.target.getBoundingClientRect();
            this.showClassificationMenu = false
            this.$nextTick(() => {
                this.classificationX = (params.left + params.width) - 200
                this.classificationY = params.bottom
                this.showClassificationMenu = true
            })
        },

        exportData() {
            const dateTitle = (() => {
                if (!isSameYear(this.$store.state.ref.dashboard.selectedRange.start, this.$store.state.ref.dashboard.selectedRange.end))
                    return `${format(this.$store.state.ref.dashboard.selectedRange.start,'dd MMM yyyy')} - ${format(this.$store.state.ref.dashboard.selectedRange.end,'dd MMM yyyy')}`
                if (!isSameMonth(this.$store.state.ref.dashboard.selectedRange.start, this.$store.state.ref.dashboard.selectedRange.end))
                    return `${format(this.$store.state.ref.dashboard.selectedRange.start,'dd MMM')} - ${format(this.$store.state.ref.dashboard.selectedRange.end,'dd MMM yyyy')}`
                if (!isSameDay(this.$store.state.ref.dashboard.selectedRange.start, this.$store.state.ref.dashboard.selectedRange.end))
                    return `${format(this.$store.state.ref.dashboard.selectedRange.start,'dd')} - ${format(this.$store.state.ref.dashboard.selectedRange.end,'dd MMM yyyy')}`
                return format(this.$store.state.ref.dashboard.selectedRange.end,'dd MMM yyyy')
            })()
            exportStats(
                    `Tracklogix Apps ${dateTitle}`,
                    'Apps', this.$refs.table.headers,
                    this.$refs.table.selectableItems,
                    this.$store.getters.labelsRef
            )
        },

        async refreshData() {
            try {
                this.loading = true;
                this.stats = await this.$newReq('POST', 'dashboard/getAppsStats', {
                    start: this.start,
                    end: this.end,
                })
            } catch (e) {
                this.$snack.netWorkError();
            } finally {
                this.loading = false
            }
        }
    },
    computed: {
        ...mapGetters(['labelsRef', 'access', 'devicesWithAccess', "labels"]),
        start() {
            return this.$store.state.ref.dashboard.selectedRange.start
        },
        end() {
            return this.$store.state.ref.dashboard.selectedRange.end
        },
        viewColumns() {

            const cols = this.headers.filter(header => {
                if (!this.$store.state.ref.dashboard.appsSelectedColumns.length)
                    return true
                return this.$store.state.ref.dashboard.appsSelectedColumns.includes(header.value)
            })

            return this.access.classification ? cols.concat({
                text: '',
                value: 'actions',
                sortable: false,
                align: 'center',
                width: 30
            })
                    : cols
        }
    },
}
</script>

<style scoped>

</style>